import React, { useState, useRef, useEffect } from 'react';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { useTheme } from '@/contexts/ThemeContext';
import Modal from '../../../../components/common/Modal';
import { usePlanFeatures } from '../../../../hooks/usePlanFeatures';
import { CONTENT_TYPE_OPTIONS } from '../wizardTypes';
import type { ContentTypeOption } from '../wizardTypes';

interface ContentTypePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (option: ContentTypeOption) => void;
}

/** Decorative swirl behind the title */
function TitleSwirl() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="373"
      height="113"
      viewBox="0 0 373 113"
      fill="none"
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] md:w-[373px] h-auto pointer-events-none select-none rotate-[6deg] z-[2]"
      aria-hidden="true"
    >
      <g opacity="0.7" filter="url(#filter0_diiii_ctp)">
        <path d="M16.132 59.8346L77.8601 32.9489C86.9725 28.9797 96.2743 23.8751 105.787 21.3275C111.94 19.6776 125.029 20.2368 122.616 31.3408C121.06 38.4987 112.027 43.3883 107.631 48.4318C101.664 55.2773 96.715 64.8489 97.9372 74.6609C100.514 95.3392 121.643 95.7116 134.096 85.6689C140.966 80.1256 146.963 73.1981 153.659 67.3628C161.884 60.1645 170.567 53.6629 179.636 47.914C186.4 43.6461 221.602 20.3303 222.621 41.0251C222.912 46.9243 215.412 49.1596 211.742 51.5969C205.393 55.8092 199.991 61.7654 198.958 70.3224C196.573 90.0836 215.186 100.915 230.269 96.1802C239.886 93.1619 248.544 86.4229 257.325 81.218C267.819 74.9984 278.471 69.144 289.281 63.6547C310.067 53.0807 331.384 43.9421 353.11 36.2913C357.7 34.6783 361.174 30.5619 360.433 24.8337C359.82 20.0903 354.951 15.2829 350.4 16.8825C311.546 30.5314 273.942 48.5727 238.176 70.7249C233.602 73.551 220.933 83.0787 217.068 74.984C214.505 69.6085 223.8 66.5362 227.046 64.3568C240.773 55.1369 244.536 36.5375 233.815 22.8602C221.15 6.70844 201.295 14.1076 186.173 22.1523C169.22 31.2057 153.266 42.5208 138.662 55.847C134.161 59.9521 124.079 73.6897 117.836 72.9965C110.06 72.1337 122.481 59.5248 124.927 57.2607C137.915 45.244 147 26.8739 134.033 10.7214C119.141 -7.8316 96.4156 3.93137 79.0069 11.5112C55.6887 21.6672 32.3693 31.8232 9.04889 41.9791C-1.57183 46.6066 5.52948 64.4506 16.1298 59.8337" fill="black" fillOpacity="0.01" shapeRendering="crispEdges"/>
      </g>
      <defs>
        <filter id="filter0_diiii_ctp" x="0" y="-0.2" width="372.3" height="112.3" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feOffset dx="4" dy="7"/><feGaussianBlur stdDeviation="3.9"/>
          <feComposite in2="hardAlpha" operator="out"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/>
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1"/>
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha2"/>
          <feOffset/><feGaussianBlur stdDeviation="11"/>
          <feComposite in2="hardAlpha2" operator="arithmetic" k2="-1" k3="1"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0.949 0 0 0 0 0.949 0 0 0 0 0.949 0 0 0 0.5 0"/>
          <feBlend mode="plus-darker" in2="shape" result="effect2"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha3"/>
          <feOffset dx="-1" dy="-1"/><feGaussianBlur stdDeviation="0.5"/>
          <feComposite in2="hardAlpha3" operator="arithmetic" k2="-1" k3="1"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0.702 0 0 0 0 0.702 0 0 0 0 0.702 0 0 0 0.19 0"/>
          <feBlend mode="plus-lighter" in2="effect2" result="effect3"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha4"/>
          <feOffset dx="1" dy="1"/><feGaussianBlur stdDeviation="0.5"/>
          <feComposite in2="hardAlpha4" operator="arithmetic" k2="-1" k3="1"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0.702 0 0 0 0 0.702 0 0 0 0 0.702 0 0 0 0.36 0"/>
          <feBlend mode="plus-lighter" in2="effect3" result="effect4"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha5"/>
          <feOffset dx="3" dy="3"/><feGaussianBlur stdDeviation="1.3"/>
          <feComposite in2="hardAlpha5" operator="arithmetic" k2="-1" k3="1"/>
          <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.23 0"/>
          <feBlend mode="normal" in2="effect4" result="effect5"/>
          <feBlend mode="normal" in="effect5" in2="effect1" result="final"/>
        </filter>
      </defs>
    </svg>
  );
}

export default function ContentTypePickerModal({
  isOpen,
  onClose,
  onSelect,
}: ContentTypePickerModalProps) {
  const { theme } = useTheme();
  const { scorm: scormAllowed } = usePlanFeatures();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = CONTENT_TYPE_OPTIONS.find((o) => o.category === selectedCategory) ?? null;

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [dropdownOpen]);

  const handleSave = () => {
    if (!selectedOption) return;
    onSelect(selectedOption);
  };

  const handleClose = () => {
    setSelectedCategory('');
    setDropdownOpen(false);
    onClose();
  };

  const hasValue = !!selectedCategory;
  const isActive = dropdownOpen || hasValue;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="" size="3xl">
      <div className="flex flex-col items-center px-4 sm:px-8 pt-2 pb-9">
        {/* "Add Content" heading with decorative swirl */}
        <div className="relative flex items-center justify-center mb-6 w-full pt-8 pb-4">
          <h2 className="text-primary-500 font-bold text-center whitespace-nowrap text-[36px] leading-[44px] sm:text-[48px] sm:leading-[56px] md:text-[64px] md:leading-[72px]">
            Add Content
          </h2>
          <TitleSwirl />
        </div>

        {/* Select dropdown */}
        <div className="w-full">
          <div className="relative">
            <label
              className={`
                absolute left-3.5 px-0.5 z-[1] pointer-events-none
                bg-surface-primary
                transition-[top,font-size,font-weight,color] duration-150
                ${isActive
                  ? '-top-2 text-xs font-semibold text-text-primary'
                  : 'top-4 text-[15px] font-normal text-text-disabled'
                }
              `}
            >
              Select the content type
            </label>

            <button
              ref={triggerRef}
              type="button"
              onClick={() => setDropdownOpen((v) => !v)}
              className={`
                w-full h-14 px-4 pr-12 rounded-full text-left text-[15px] font-medium
                cursor-pointer transition-[border-color,background-color] duration-150
                ${isActive
                  ? 'border border-primary-500 bg-primary-500/[0.08]'
                  : 'border border-grey-500/20 bg-surface-primary'
                }
                ${hasValue ? 'text-text-primary' : 'text-transparent'}
              `}
            >
              {selectedOption?.label ?? '\u00A0'}
            </button>

            <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none flex items-center">
              <svg
                width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"
                className={`text-text-secondary transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
              >
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </div>

          {dropdownOpen && (
            <div
              ref={dropdownRef}
              className="mt-2 w-full rounded-2xl p-4 flex flex-col gap-0.5"
              style={{
                background: theme === 'dark'
                  ? 'linear-gradient(203deg, rgb(var(--color-primary) / 0.15) 37%, rgb(var(--color-primary) / 0.25) 73.6%, rgb(var(--color-primary) / 0.2) 92.2%, rgb(var(--color-primary) / 0.3) 107.4%), rgb(var(--color-surface-primary))'
                  : 'linear-gradient(203deg, rgba(255,231,208,0) 37%, rgba(255,231,208,0.3) 73.6%, rgba(255,203,221,0.3) 92.2%, rgb(var(--color-primary) / 0.3) 107.4%), #fff',
                boxShadow: '4px 7px 28px 0px rgba(0,0,0,0.1)',
              }}
            >
              {CONTENT_TYPE_OPTIONS.map((option) => {
                const isSelected = option.category === selectedCategory;
                // Staff may see locked plan features (with the plan tag) —
                // selecting them is what's blocked.
                const locked = option.category === 'upload_scorm' && !scormAllowed;
                return (
                  <button
                    key={option.category}
                    type="button"
                    disabled={locked}
                    title={locked ? 'SCORM is available from the Pro plan' : undefined}
                    onClick={() => {
                      setSelectedCategory(option.category);
                      setDropdownOpen(false);
                    }}
                    className={`
                      w-full px-2 py-1.5 text-left text-sm font-semibold
                      rounded-full transition-colors duration-100
                      ${locked
                        ? 'bg-grey-500/[0.16] text-text-disabled cursor-not-allowed'
                        : isSelected
                          ? 'bg-primary-500/40 text-text-primary cursor-pointer'
                          : 'bg-grey-500/[0.16] text-text-primary hover:bg-grey-500/25 cursor-pointer'
                      }
                    `}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {option.label}
                      {locked && (
                        <>
                          <LockClosedIcon className="w-3.5 h-3.5" aria-hidden="true" />
                          <span className="text-[11px] font-bold uppercase tracking-wide text-primary-600 dark:text-primary-350">
                            Pro
                          </span>
                        </>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div className="flex items-center justify-center gap-4 mt-8">
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex items-center gap-1.5 px-5 py-1.5 rounded-full border border-secondary text-secondary text-sm font-bold hover:bg-secondary/5 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={!selectedOption}
            className="inline-flex items-center gap-1.5 px-5 py-1.5 rounded-full text-white text-sm font-bold min-w-[100px] justify-center disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: selectedOption
                ? `linear-gradient(to top, ${'rgb(var(--color-primary-700))'}, rgb(var(--color-primary)) 70%)`
                : undefined,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
}
